import type { ReduxApplicationState } from '../combinedReducer';
import { initialListState } from './slice';

/**
 * Whether the client-fetched fields belong to the product the page is showing. They do not
 * on the first render after a client-side navigation: the props deep-merge has replaced the
 * product but left the previous product's fields, and the effect that resets them has not
 * run yet. Reading them through this keeps that render from showing the wrong product.
 */
const isForCurrentProduct = (state: ReduxApplicationState) =>
  state.reviews.productCode === state.product.code;

// The summary selectors are the data layer's read API for the ReviewsSummary
// component (DISC-4325's split, consumed from DISC-4326).
export const selectAiSummary = (state: ReduxApplicationState) => state.reviews.aiSummary;

export const selectProductInsights = (state: ReduxApplicationState) =>
  isForCurrentProduct(state) ? state.reviews.productInsights : undefined;

export const selectRatingBreakdown = (state: ReduxApplicationState) =>
  isForCurrentProduct(state) ? state.reviews.ratingBreakdown : undefined;

export const selectReviewsList = (state: ReduxApplicationState) =>
  isForCurrentProduct(state) ? state.reviews.list : initialListState;

/** The reviews with photos, for the customers' photos strip. Undefined until settled. */
export const selectPhotoReviews = (state: ReduxApplicationState) =>
  isForCurrentProduct(state) ? state.reviews.photoReviews : undefined;

/** The unfiltered first page for the preview row; the drawer's filter does not touch it. */
export const selectFirstReviewsPage = (state: ReduxApplicationState) =>
  selectReviewsList(state).firstPage;

export const selectReviewsListError = (state: ReduxApplicationState) =>
  selectReviewsList(state).error;

/**
 * The breakdown and the list are the same Feefo data but arrive separately, so whichever
 * has landed is used; the list's total is from its last unfiltered page, since `meta`
 * follows the star filter. `||` because a zero from either means "none yet".
 */
export const selectReviewsTotalCount = (state: ReduxApplicationState) =>
  selectRatingBreakdown(state)?.totalCount || selectReviewsList(state).totalCount || 0;

/**
 * Feefo's breakdown alone, since the list carries no average. Null until it settles, which
 * hides the pill rather than showing zero stars.
 */
export const selectReviewsAverageRating = (state: ReduxApplicationState) =>
  selectRatingBreakdown(state)?.averageRating || null;
